import { Component, ErrorInfo, ReactNode } from 'react';
import { errorService } from '../services/errorService';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * Error boundary component to catch React rendering errors
 * Wraps component tree to prevent entire app crashes
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Log error to error service
        errorService.logError('React component error', error, {
            component: errorInfo.componentStack || undefined,
            metadata: {
                componentStack: errorInfo.componentStack || undefined
            }
        });

        // Call optional error handler
        this.props.onError?.(error, errorInfo);
    }

    render(): ReactNode {
        if (this.state.hasError) {
            // Render fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={{
                    padding: '2rem',
                    margin: '1rem',
                    border: '1px solid #f5222d',
                    borderRadius: '4px',
                    backgroundColor: '#fff2f0'
                }}>
                    <h2 style={{ color: '#cf1322', marginTop: 0 }}>Something went wrong</h2>
                    <p style={{ marginBottom: '1rem' }}>
                        An unexpected error occurred. Please refresh the page or contact support if the problem persists.
                    </p>
                    {import.meta.env.DEV && this.state.error && (
                        <details style={{ marginTop: '1rem' }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                Error details (development only)
                            </summary>
                            <pre style={{
                                marginTop: '0.5rem',
                                padding: '1rem',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                overflow: 'auto',
                                fontSize: '0.875rem'
                            }}>
                                {this.state.error.toString()}
                                {'\n\n'}
                                {this.state.error.stack}
                            </pre>
                        </details>
                    )}
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: '1rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: '#1890ff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

